import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '@/components/common/ConfirmModal';
import MoreActionMenu, { ActionMenuItem } from '@/components/common/MoreActionMenu';
import Toast from '@/components/common/Toast';
import { useAuth } from '@/hooks/useAuth';
import { useAssetRenewStore } from '@/stores';
import {
  createPreviewImageFromFile,
  uploadFile,
  revokeImageUrl,
  PreviewImage,
} from '@/utils/upload';
import {
  fetchComments,
  fetchCommentReplies,
  createComment,
  createCommentReply,
  pinComment,
  unpinComment,
  deleteComment,
} from '@/service/api/community';

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `idempotency-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const inFlightCommentsRequests = new Map<string, Promise<Api.Community.CommentPageResponse>>();

function getCommentsDeduplicated(
  params: Api.Community.QueryCommentsParams
): Promise<Api.Community.CommentPageResponse> {
  const key = JSON.stringify(params);
  if (inFlightCommentsRequests.has(key)) {
    return inFlightCommentsRequests.get(key)!;
  }
  const promise = fetchComments(params).finally(() => {
    inFlightCommentsRequests.delete(key);
  });
  inFlightCommentsRequests.set(key, promise);
  return promise;
}

export function formatCommentTime(iso?: string): string {
  if (!iso) return '未知时间';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins} 分钟前`;
  if (hrs < 24) return `${hrs} 小时前`;
  if (days < 7) return `${days} 天前`;
  return new Date(iso).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
}

export interface CommentSectionProps {
  /** 评论目标类型 (例如 'COMMUNITY_POST', 'DEMAND', 'LEARNING_RESOURCE', 'JOB_POSTING', 'PROVIDER_PROFILE') */
  targetType: Api.Community.CommentTargetType | string;
  /** 评论目标 ID */
  targetId: string;
  /** 初始评论数 (可选) */
  initialCommentCount?: number;
  /** 当前登录用户是否为该目标的主人/作者 (例如贴主/需求发布人)，拥有置顶和删除评论权限 */
  isTargetAuthor?: boolean;
  /** 大图预览回调 (可选) */
  onPreviewImage?: (url: string) => void;
  /** 评论总数变更时的回调 */
  onCommentCountChange?: (count: number) => void;
  /** 容器自定义 class */
  className?: string;
  /** 标题文字 (默认: "评论") */
  title?: string;
}

export default function CommentSection({
  targetType,
  targetId,
  initialCommentCount = 0,
  isTargetAuthor = false,
  onPreviewImage,
  onCommentCountChange,
  className = '',
  title = '评论',
}: CommentSectionProps) {
  const { isLoggedIn, user, rawUser } = useAuth();
  const navigate = useNavigate();
  const userAvatarCache = useAssetRenewStore((state) => state.userAvatarCache);

  const userAvatarUrl =
    userAvatarCache?.url ||
    (user as any)?.avatar_access_url ||
    (rawUser as any)?.avatar_access_url ||
    (typeof user?.avatar === 'object'
      ? (user.avatar as any)?.access_url
      : typeof user?.avatar === 'string' &&
        (user.avatar.startsWith('http') ||
          user.avatar.startsWith('data:') ||
          user.avatar.startsWith('blob:') ||
          user.avatar.includes('/'))
        ? user.avatar
        : undefined);

  const userDisplayName = user?.name || (user as any)?.display_name || (rawUser as any)?.display_name || '我';

  const currentUserId = user?.id || (user as unknown as { account_id?: string })?.account_id;

  const [comments, setComments] = useState<Api.Community.CommentResponse[]>([]);
  const [commentCount, setCommentCount] = useState<number>(initialCommentCount);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [newComment, setNewComment] = useState('');
  const [sortBy, setSortBy] = useState<'hot' | 'new'>('hot');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [commentImages, setCommentImages] = useState<PreviewImage[]>([]);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);

  // Toast 提示 State
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  } | null>(null);

  const showToast = (
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'info'
  ) => {
    setToast({ message, type });
  };

  // 内置的大图预览 State（如果外部未提供 onPreviewImage 则使用内置 Modal）
  const [internalPreviewUrl, setInternalPreviewUrl] = useState<string | null>(null);

  const handleImageClick = (url: string) => {
    if (onPreviewImage) {
      onPreviewImage(url);
    } else {
      setInternalPreviewUrl(url);
    }
  };

  // 同步更新外部评论数量
  const updateCommentCount = (updater: number | ((prev: number) => number)) => {
    setCommentCount((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const validNext = Math.max(0, next);
      onCommentCountChange?.(validNext);
      return validNext;
    });
  };

  useEffect(() => {
    if (!targetId) return;
    let cancelled = false;
    setLoading(true);

    getCommentsDeduplicated({
      target_type: targetType as Api.Community.CommentTargetType,
      target_id: targetId,
      page_size: 50,
    })
      .then((res) => {
        if (!cancelled) {
          const list = [...(res?.pinned_comments || []), ...(res?.items || [])];
          setComments(list);
          if (typeof res?.comment_count === 'number') {
            updateCommentCount(res.comment_count);
          } else {
            updateCommentCount(list.length);
          }
        }
      })
      .catch((err) => {
        console.error('获取评论列表失败:', err);
        if (!cancelled) {
          setComments([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [targetType, targetId]);

  // 监听评论列表变化，自动收集并批量续发评论图片与作者头像
  useEffect(() => {
    if (!comments.length) return;

    // 全量收集主评论及二级回复项
    const allCommentItems: Api.Community.CommentResponse[] = [];
    comments.forEach((c) => {
      allCommentItems.push(c);
      if (c.replies && c.replies.length) {
        c.replies.forEach((r) => allCommentItems.push(r));
      }
    });

    const renewRequests: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];
    const singleAssetIds: string[] = [];

    // 1. 优先将评论附件与评论作者头像的初始 access_url 集中注册至 Store 缓存
    allCommentItems.forEach((c) => {
      (c.attachments || []).forEach((att) => {
        const assetId = att.asset_id || att.asset?.id;
        const url = att.url || att.asset?.access_url || (att as any).access_url;
        const expires = att.asset?.access_expires;
        if (assetId && url) {
          useAssetRenewStore.getState().setAssetCacheItem(String(assetId), url, expires);
        }
      });

      const authorAvatarAssetId = c.author?.avatar_asset_id || c.author?.avatar?.id;
      const authorAvatarUrl =
        c.author?.avatar_access_url ||
        c.author?.avatar?.access_url ||
        (c.author as any)?.avatar_url;
      const authorAvatarExpires = (c.author?.avatar as any)?.access_expires;

      if (authorAvatarAssetId && authorAvatarUrl) {
        useAssetRenewStore.getState().setAssetCacheItem(String(authorAvatarAssetId), authorAvatarUrl, authorAvatarExpires);
      }
    });

    // 2. 收集资源上下文（评论附件图片使用 COMMENT 资源类型，评论作者头像优先使用 access_resource 或 ACCOUNT）
    allCommentItems.forEach((c) => {
      const commentId = String(c.id || '');
      const attAssetIds = (c.attachments || [])
        .map((att) => att.asset_id || att.asset?.id)
        .filter(Boolean) as string[];

      // 评论图片附件使用 COMMENT 资源类型
      if (commentId && attAssetIds.length) {
        renewRequests.push({
          resource_type: 'COMMENT',
          resource_id: commentId,
          asset_ids: attAssetIds,
        });
      }

      // 评论作者头像：优先使用后端下发的 access_resource 或回退为 ACCOUNT 账号 ID 上下文
      const authorAvatarAssetId = c.author?.avatar_asset_id || c.author?.avatar?.id;
      if (authorAvatarAssetId) {
        const avatarAccessResource = (c.author?.avatar as any)?.access_resource;
        if (avatarAccessResource && avatarAccessResource.resource_type && avatarAccessResource.resource_id) {
          renewRequests.push({
            resource_type: avatarAccessResource.resource_type,
            resource_id: String(avatarAccessResource.resource_id),
            asset_ids: avatarAccessResource.asset_ids?.length
              ? avatarAccessResource.asset_ids.map(String)
              : [String(authorAvatarAssetId)],
          });
        } else {
          const accountId = String(c.author_account_id || c.author?.account_id || '');
          if (accountId) {
            renewRequests.push({
              resource_type: 'ACCOUNT',
              resource_id: accountId,
              asset_ids: [String(authorAvatarAssetId)],
            });
          } else {
            singleAssetIds.push(String(authorAvatarAssetId));
          }
        }
      }
    });

    if (renewRequests.length) {
      useAssetRenewStore.getState().renewBatchResources(renewRequests).catch(() => { });
    }

    singleAssetIds.forEach((assetId) => {
      useAssetRenewStore.getState().renewSingleAsset(assetId, 'ACCOUNT').catch(() => { });
    });
  }, [comments]);

  const handleSelectImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (commentImages.length + files.length > 3) {
      showToast('评论最多只能上传 3 张图片', 'warning');
      return;
    }

    setUploadingImage(true);
    try {
      for (const file of files) {
        const preview = createPreviewImageFromFile(file);
        setCommentImages((prev) => [...prev, preview]);
        try {
          const uploaded = await uploadFile(file, 'COMMENT');
          const assetId = String(uploaded.asset_id || uploaded.upload_asset_id || '');
          setCommentImages((prev) =>
            prev.map((img) =>
              img.id === preview.id
                ? { ...img, asset_id: assetId, upload_asset_id: assetId, url: uploaded.file_url || img.url }
                : img
            )
          );
        } catch (err) {
          console.error('上传评论图片失败:', err);
          setCommentImages((prev) => prev.filter((img) => img.id !== preview.id));
          revokeImageUrl(preview);
          showToast(`图片 ${file.name} 上传失败`, 'error');
        }
      }
    } finally {
      setUploadingImage(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoveImage = (id: string) => {
    setCommentImages((prev) => {
      const target = prev.find((img) => img.id === id);
      if (target) revokeImageUrl(target);
      return prev.filter((img) => img.id !== id);
    });
  };

  // 目标作者专属：切换评论置顶状态
  const handleTogglePinComment = async (commentId: string, currentPinned?: boolean) => {
    if (!commentId) return;
    const idempotencyKey = generateIdempotencyKey();
    try {
      if (currentPinned) {
        await unpinComment(commentId, idempotencyKey);
      } else {
        await pinComment(commentId, idempotencyKey);
      }
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, is_pinned: !currentPinned } : c))
      );
    } catch (err) {
      console.error('切换评论置顶状态失败:', err);
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, is_pinned: !currentPinned } : c))
      );
    }
  };

  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [deletingCommentLoading, setDeletingCommentLoading] = useState(false);

  // 删除一级评论 modal 打开与执行
  const handleOpenDeleteCommentModal = (commentId: string) => {
    if (!commentId) return;
    setDeletingCommentId(commentId);
  };

  const handleExecuteDeleteComment = async () => {
    if (!deletingCommentId || deletingCommentLoading) return;
    setDeletingCommentLoading(true);
    const idempotencyKey = generateIdempotencyKey();
    try {
      await deleteComment(deletingCommentId, idempotencyKey);
      setComments((prev) => prev.filter((c) => c.id !== deletingCommentId));
      updateCommentCount((prev) => prev - 1);
    } catch (err) {
      console.error('删除评论失败:', err);
      setComments((prev) => prev.filter((c) => c.id !== deletingCommentId));
      updateCommentCount((prev) => prev - 1);
    } finally {
      setDeletingCommentLoading(false);
      setDeletingCommentId(null);
    }
  };

  const sortedComments = [...comments].sort((a, b) => {
    if (sortBy === 'new') {
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    }
    return (b.reply_count || 0) - (a.reply_count || 0);
  });

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      navigate('/auth');
      return;
    }
    if (!newComment.trim() || !targetId || submitting || uploadingImage) return;

    setSubmitting(true);
    const text = newComment.trim();
    const idempotencyKey = generateIdempotencyKey();
    const imageAssetIds = commentImages
      .map((img) => img.asset_id || img.upload_asset_id)
      .filter(Boolean) as (string | number)[];

    const currentAttachments: Api.Community.AttachmentResponse[] = commentImages.map((img, idx) => ({
      asset_id: String(img.asset_id || img.upload_asset_id || ''),
      sort_no: idx,
      url: img.url,
      asset: {
        id: String(img.asset_id || img.upload_asset_id || ''),
        access_url: img.url,
        file_name: img.file_name,
        file_size: img.file_size,
        mime_type: img.file_type,
      },
    }));

    try {
      const res: any = await createComment(
        {
          target_type: targetType as Api.Community.CommentTargetType,
          target_id: targetId,
          content: text,
          image_asset_ids: imageAssetIds.length > 0 ? imageAssetIds : undefined,
        },
        idempotencyKey
      );

      if (res && res.code !== undefined && res.code !== 200) {
        showToast(res.message || res.msg || '发送评论失败', 'error');
        return;
      }

      if (res && res.id) {
        const fullRes = {
          ...res,
          attachments: res.attachments && res.attachments.length > 0 ? res.attachments : currentAttachments,
        };
        setComments((prev) => [fullRes, ...prev]);
        updateCommentCount((prev) => prev + 1);
        setNewComment('');
        setCommentImages([]);
      } else {
        const fallbackComment: Api.Community.CommentResponse = {
          id: `cmt-${Date.now()}`,
          content: text,
          created_at: new Date().toISOString(),
          is_target_author: isTargetAuthor,
          attachments: currentAttachments,
          author: {
            display_name: user?.name || '我',
            avatar_asset_id: user?.avatar,
          },
        };
        setComments((prev) => [fallbackComment, ...prev]);
        updateCommentCount((prev) => prev + 1);
        setNewComment('');
        setCommentImages([]);
      }
    } catch (err: any) {
      console.error('发表一级评论失败:', err);
      const errorMsg = err?.message || err?.msg || '发送评论失败';
      showToast(errorMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={`bg-white rounded-2xl p-6 md:p-8 border border-background-200/70 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
          <i className="ri-chat-3-line text-primary-500"></i>
          {title}
          <span className="text-sm font-normal text-foreground-400">({commentCount})</span>
        </h3>
        {/* <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
          <button
            onClick={() => setSortBy('hot')}
            className={`text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer ${
              sortBy === 'hot' ? 'bg-white text-foreground-950 font-medium shadow-sm' : 'text-foreground-500'
            }`}
          >
            最热
          </button>
          <button
            onClick={() => setSortBy('new')}
            className={`text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer ${
              sortBy === 'new' ? 'bg-white text-foreground-950 font-medium shadow-sm' : 'text-foreground-500'
            }`}
          >
            最新
          </button>
        </div> */}
      </div>

      {/* Comment Form */}
      {!isLoggedIn ? (
        <div className="mb-8 p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-slate-50 via-slate-50/80 to-emerald-50/30 border border-slate-200/80 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 transition-all">
          <div className="flex items-center gap-3.5 text-center sm:text-left">
            <div className="w-11 h-11 rounded-full bg-emerald-100/80 text-emerald-600 border border-emerald-200/60 flex items-center justify-center flex-shrink-0 shadow-inner">
              <i className="ri-user-3-line text-xl"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">未登录社区账号</p>
              <p className="text-xs text-slate-500 mt-0.5">登录后即可发表评论、参与互动与交流想法</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/auth')}
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white text-sm font-semibold transition-all shadow-[0_4px_14px_rgba(16,185,129,0.3)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.4)] flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-login-box-line text-base"></i>
            <span>登录 / 注册</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmitComment} className="flex gap-3 mb-8">
          {userAvatarUrl ? (
            <img
              src={userAvatarUrl}
              alt={userDisplayName}
              className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-xs flex-shrink-0 mt-0.5 select-none">
              {userDisplayName.trim().slice(0, 1).toUpperCase() || '我'}
            </div>
          )}
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="写下你的想法..."
              maxLength={500}
              className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-gray-50 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
              rows={2}
            />

            {/* Comment Image Previews */}
            {commentImages.length > 0 && (
              <div className="flex flex-wrap gap-2.5 mt-2 mb-1">
                {commentImages.map((img) => (
                  <div key={img.id} className="relative w-16 h-16 rounded-xl overflow-hidden border border-background-200 bg-black/5 group">
                    <img src={img.url} alt="评论图片" className="w-full h-full object-cover" />
                    {!img.asset_id && (
                      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center text-white">
                        <i className="ri-loader-4-line text-sm animate-spin"></i>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(img.id)}
                      className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/60 text-white flex items-center justify-center text-xs opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleSelectImages}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={commentImages.length >= 3 || uploadingImage}
                  className="text-foreground-400 hover:text-primary-600 transition-colors text-sm flex items-center gap-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  title="添加图片 (最多3张)"
                >
                  <i className="ri-image-add-line text-lg"></i>
                  <span className="text-xs">{commentImages.length > 0 ? `${commentImages.length}/3` : '图片'}</span>
                </button>
                <span className="text-xs text-foreground-400">{newComment.length}/500</span>
              </div>

              <button
                type="submit"
                disabled={!newComment.trim() || submitting || uploadingImage}
                className="px-5 py-2 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:brightness-95 active:brightness-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 shadow-sm"
              >
                {submitting ? (
                  <i className="ri-loader-4-line text-base animate-spin"></i>
                ) : (
                  <i className="ri-send-plane-line text-base"></i>
                )}
                发送
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-foreground-400 gap-2 text-sm">
          <i className="ri-loader-4-line text-xl animate-spin text-primary-500"></i>
          加载评论中...
        </div>
      ) : sortedComments.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
            <i className="ri-chat-off-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">暂无评论，来说两句吧</p>
        </div>
      ) : (
        <div className="space-y-5">
          {sortedComments.map((comment) => (
            <CommentItem
              key={comment.id || comment.created_at}
              comment={comment}
              isTargetAuthor={isTargetAuthor}
              currentUserId={currentUserId}
              onPreviewImage={handleImageClick}
              onTogglePin={() => handleTogglePinComment(comment.id || '', comment.is_pinned)}
              onDeleteComment={() => handleOpenDeleteCommentModal(comment.id || '')}
              onSubCommentCountChange={(delta) => updateCommentCount((prev) => prev + delta)}
              onShowToast={showToast}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        open={Boolean(deletingCommentId)}
        title="删除评论"
        content="确定要删除此评论吗？此操作无法撤销。"
        type="danger"
        confirmText="确定删除"
        cancelText="取消"
        loading={deletingCommentLoading}
        onConfirm={handleExecuteDeleteComment}
        onCancel={() => setDeletingCommentId(null)}
      />

      {/* 内置全屏图片预览 Modal（无外部 onPreviewImage 时备用） */}
      {internalPreviewUrl && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setInternalPreviewUrl(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] flex items-center justify-center">
            <img
              src={internalPreviewUrl}
              alt="大图预览"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={() => setInternalPreviewUrl(null)}
              className="absolute -top-10 right-0 text-white text-3xl hover:text-gray-300 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </section>
  );
}

interface CommentItemProps {
  comment: Api.Community.CommentResponse;
  isTargetAuthor: boolean;
  currentUserId?: string;
  onPreviewImage?: (url: string) => void;
  onTogglePin?: () => void;
  onDeleteComment?: () => void;
  onSubCommentCountChange?: (delta: number) => void;
  onShowToast?: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
}

function CommentItem({
  comment,
  isTargetAuthor,
  currentUserId,
  onPreviewImage,
  onTogglePin,
  onDeleteComment,
  onSubCommentCountChange,
  onShowToast,
}: CommentItemProps) {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const assetCache = useAssetRenewStore((state) => state.assetCache);

  const authorName = comment.author?.display_name || '社区用户';
  const rawAuthorAvatar =
    comment.author?.avatar?.access_url ||
    comment.author?.avatar_access_url ||
    (comment as any).author_avatar_access_url ||
    comment.author?.avatar_asset_id;

  const authorAvatarUrl =
    (comment.author?.avatar_asset_id && assetCache[comment.author.avatar_asset_id]?.url) ||
    rawAuthorAvatar;

  const hasAuthorAvatar = Boolean(
    authorAvatarUrl &&
    (authorAvatarUrl.startsWith('http') ||
      authorAvatarUrl.startsWith('data:') ||
      authorAvatarUrl.startsWith('blob:') ||
      authorAvatarUrl.includes('/'))
  );

  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyTarget, setReplyTarget] = useState<{ id: string; name: string } | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [replies, setReplies] = useState<Api.Community.CommentResponse[]>(comment.replies || []);
  const [replyCount, setReplyCount] = useState<number>(comment.reply_count || 0);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyCursor, setReplyCursor] = useState<string | undefined>(undefined);

  useEffect(() => {
    setReplyCount(comment.reply_count || 0);
  }, [comment.reply_count]);

  const hasMoreReplies = replyCount > replies.length;

  const handleLoadMoreReplies = async () => {
    if (!comment.id || loadingReplies) return;
    setLoadingReplies(true);
    try {
      const res = await fetchCommentReplies(comment.id, {
        cursor: replyCursor,
        page_size: 20,
      });
      if (res && res.items && res.items.length > 0) {
        setReplies((prev) => {
          const existingIds = new Set(prev.map((r) => r.id).filter(Boolean));
          const newItems = res.items!.filter((item) => !item.id || !existingIds.has(item.id));
          const updated = [...prev, ...newItems];
          if (updated.length > replyCount) {
            setReplyCount(updated.length);
          }
          return updated;
        });
        if (res.next_cursor) {
          setReplyCursor(res.next_cursor);
        }
      }
    } catch (err) {
      console.error('加载更多二级回复失败:', err);
    } finally {
      setLoadingReplies(false);
    }
  };

  const commentAuthorId = comment.author_account_id || comment.author?.account_id;
  const isCommentOwner = Boolean(currentUserId && commentAuthorId && currentUserId === commentAuthorId);
  const canDeleteComment = isTargetAuthor || isCommentOwner;

  const handleToggleReplyRoot = () => {
    if (!isLoggedIn && !showReplyBox) {
      navigate('/auth');
      return;
    }
    if (showReplyBox && replyTarget?.id === comment.id) {
      setShowReplyBox(false);
      setReplyTarget(null);
    } else {
      setReplyTarget({ id: comment.id || '', name: authorName });
      setShowReplyBox(true);
    }
  };

  const handleReplyToSubComment = (subCommentId: string, subAuthorName: string) => {
    if (!isLoggedIn) {
      navigate('/auth');
      return;
    }
    setReplyTarget({ id: subCommentId, name: subAuthorName });
    setShowReplyBox(true);
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      navigate('/auth');
      return;
    }
    if (!replyText.trim() || !comment.id || submittingReply) return;

    setSubmittingReply(true);
    const text = replyText.trim();
    const targetId = replyTarget?.id || comment.id;
    const targetName = replyTarget?.name || authorName;
    const idempotencyKey = generateIdempotencyKey();

    try {
      const res: any = await createCommentReply(
        comment.id,
        {
          content: text,
          reply_to_comment_id: targetId,
        },
        idempotencyKey
      );

      if (res && res.code !== undefined && res.code !== 200) {
        onShowToast?.(res.message || res.msg || '发表回复失败', 'error');
        return;
      }

      const newReplyObj: Api.Community.CommentResponse =
        res && res.id
          ? res
          : {
            id: `reply-${Date.now()}`,
            content: text,
            created_at: new Date().toISOString(),
            is_target_author: isTargetAuthor,
            author: {
              display_name: user?.name || '我',
              avatar_asset_id: user?.avatar,
            },
            reply_to: {
              comment_id: targetId,
              display_name: targetName,
            },
          };

      setReplies((prev) => [...prev, newReplyObj]);
      setReplyCount((prev) => prev + 1);
      onSubCommentCountChange?.(1);
      setReplyText('');
      setShowReplyBox(false);
      setReplyTarget(null);
    } catch (err: any) {
      console.error('发表二级回复失败:', err);
      const errorMsg = err?.message || err?.msg || '发表回复失败';
      onShowToast?.(errorMsg, 'error');
    } finally {
      setSubmittingReply(false);
    }
  };

  const [deletingSubCommentId, setDeletingSubCommentId] = useState<string | null>(null);
  const [deletingSubCommentLoading, setDeletingSubCommentLoading] = useState(false);

  // 删除二级回复 modal 打开与执行
  const handleOpenDeleteSubCommentModal = (subCommentId: string) => {
    if (!subCommentId) return;
    setDeletingSubCommentId(subCommentId);
  };

  const handleExecuteDeleteSubComment = async () => {
    if (!deletingSubCommentId || deletingSubCommentLoading) return;
    setDeletingSubCommentLoading(true);
    const idempotencyKey = generateIdempotencyKey();
    try {
      await deleteComment(deletingSubCommentId, idempotencyKey);
      setReplies((prev) => prev.filter((r) => r.id !== deletingSubCommentId));
      setReplyCount((prev) => Math.max(0, prev - 1));
      onSubCommentCountChange?.(-1);
    } catch (err) {
      console.error('删除二级回复失败:', err);
      setReplies((prev) => prev.filter((r) => r.id !== deletingSubCommentId));
      setReplyCount((prev) => Math.max(0, prev - 1));
      onSubCommentCountChange?.(-1);
    } finally {
      setDeletingSubCommentLoading(false);
      setDeletingSubCommentId(null);
    }
  };

  const commentActionItems: ActionMenuItem[] = [];
  if (isTargetAuthor) {
    commentActionItems.push({
      key: 'pin-comment',
      label: comment.is_pinned ? '取消置顶' : '置顶评论',
      icon: comment.is_pinned ? 'ri-pushpin-fill text-primary-500' : 'ri-pushpin-line',
      onClick: () => onTogglePin?.(),
    });
  }
  if (canDeleteComment) {
    commentActionItems.push({
      key: 'delete-comment',
      label: '删除评论',
      icon: 'ri-delete-bin-line',
      danger: true,
      onClick: () => onDeleteComment?.(),
    });
  }

  const isCommentDeleted = comment.status?.toUpperCase() === 'DELETED';

  return (
    <div className="flex gap-3">
      {hasAuthorAvatar ? (
        <img
          src={authorAvatarUrl}
          alt={authorName}
          className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5"
          loading="lazy"
        />
      ) : (
        <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-sm flex-shrink-0 mt-0.5 select-none">
          {authorName.trim().slice(0, 1)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-sm text-foreground-950">{authorName}</span>
          {comment.is_target_author && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200/60 font-medium">
              作者
            </span>
          )}
          {comment.is_pinned && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 font-medium flex items-center gap-0.5">
              <i className="ri-pushpin-fill text-xs"></i>
              置顶
            </span>
          )}
          <span className="text-xs text-foreground-400">{formatCommentTime(comment.published_at || comment.created_at)}</span>
        </div>
        <p className={`text-sm leading-relaxed mb-2 ${isCommentDeleted ? 'text-foreground-400' : 'text-foreground-700'}`}>
          {comment.content}
        </p>

        {/* Comment Attachment Images */}
        {comment.attachments && comment.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 my-2.5">
            {comment.attachments.map((att, idx) => {
              const assetId = att.asset_id || att.asset?.id;
              const imgUrl =
                (assetId && assetCache[assetId]?.url) ||
                att.asset?.access_url ||
                (att as unknown as { access_url?: string }).access_url ||
                att.url ||
                '';
              if (!imgUrl) return null;
              return (
                <img
                  key={assetId || idx}
                  src={imgUrl}
                  alt="评论附图"
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover border border-background-200/80 cursor-pointer hover:opacity-90 transition-opacity"
                  loading="lazy"
                  onClick={() => onPreviewImage?.(imgUrl)}
                />
              );
            })}
          </div>
        )}

        {/* Actions (回复 + 更多操作 MoreActionMenu 气泡菜单) */}
        {!isCommentDeleted && (
          <div className="flex items-center justify-between mt-1">
            <button
              onClick={handleToggleReplyRoot}
              className={`text-xs transition-colors cursor-pointer flex items-center ${showReplyBox && replyTarget?.id === comment.id
                ? 'text-primary-600 font-medium'
                : 'text-foreground-400 hover:text-foreground-600'
                }`}
            >
              <i className="ri-reply-line text-sm mr-0.5"></i>
              {showReplyBox && replyTarget?.id === comment.id ? '取消回复' : '回复'}
            </button>

            <MoreActionMenu align="right" items={commentActionItems} />
          </div>
        )}

        {/* Inline Reply Form */}
        {!isCommentDeleted && showReplyBox && (
          <form onSubmit={handleSubmitReply} className="mt-3 flex gap-2">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={`回复 @${replyTarget?.name || authorName}...`}
              maxLength={300}
              className="flex-1 px-3 py-1.5 rounded-lg border border-background-300 bg-gray-50 text-xs text-foreground-950 outline-none focus:border-primary-400 focus:bg-white transition-all"
              autoFocus
            />
            <button
              type="submit"
              disabled={!replyText.trim() || submittingReply}
              className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-medium hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 shadow-sm"
            >
              {submittingReply ? (
                <i className="ri-loader-4-line text-xs animate-spin"></i>
              ) : (
                <i className="ri-send-plane-line text-xs"></i>
              )}
              发送
            </button>
            <button
              type="button"
              onClick={() => {
                setShowReplyBox(false);
                setReplyTarget(null);
              }}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-foreground-500 text-xs hover:bg-gray-200 transition-colors cursor-pointer"
            >
              取消
            </button>
          </form>
        )}

        {/* Secondary Replies List */}
        {!isCommentDeleted && (replies.length > 0 || hasMoreReplies) && (
          <div className="mt-3 space-y-3 pt-3 border-t border-background-200/60 pl-2">
            {replies.map((reply) => {
              const rName = reply.author?.display_name || '社区用户';
              const rAvatarAssetId = reply.author?.avatar_asset_id || reply.author?.avatar?.id;
              const rawRAvatarUrl =
                reply.author?.avatar?.access_url ||
                reply.author?.avatar_access_url ||
                (reply as any).author_avatar_access_url ||
                reply.author?.avatar_asset_id;
              const rAvatarUrl =
                (rAvatarAssetId && assetCache[String(rAvatarAssetId)]?.url) ||
                rawRAvatarUrl;
              const hasRAvatar = Boolean(
                rAvatarUrl &&
                (rAvatarUrl.startsWith('http') ||
                  rAvatarUrl.startsWith('data:') ||
                  rAvatarUrl.startsWith('blob:') ||
                  rAvatarUrl.includes('/'))
              );

              const replyAuthorId = reply.author_account_id || reply.author?.account_id;
              const isReplyOwner = Boolean(currentUserId && replyAuthorId && currentUserId === replyAuthorId);
              const canDeleteReply = isTargetAuthor || isReplyOwner;
              const isReplyDeleted = reply.status?.toUpperCase() === 'DELETED';

              return (
                <div key={reply.id || reply.created_at} className="flex gap-2 text-xs">
                  {hasRAvatar ? (
                    <img src={rAvatarUrl} alt={rName} className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-[10px] flex-shrink-0 mt-0.5 select-none">
                      {rName.trim().slice(0, 1)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0 bg-gray-50 rounded-xl p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="font-semibold text-foreground-950">{rName}</span>
                      {reply.is_target_author && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200/60 font-medium">
                          作者
                        </span>
                      )}
                      {reply.reply_to?.display_name && (
                        <span className="text-foreground-400 font-normal">
                          回复 <span className="text-primary-600 font-medium">@{reply.reply_to.display_name}</span>
                        </span>
                      )}
                      <span className="text-foreground-400 ml-auto text-[10px]">
                        {formatCommentTime(reply.published_at || reply.created_at)}
                      </span>
                    </div>
                    <p className={`leading-relaxed mb-1.5 ${isReplyDeleted ? 'text-foreground-400' : 'text-foreground-700'}`}>
                      {reply.content}
                    </p>

                    {/* 二级评论操作 */}
                    {!isReplyDeleted && (
                      <div className="flex items-center justify-between mt-1">
                        <button
                          onClick={() => handleReplyToSubComment(reply.id || '', rName)}
                          className="text-[11px] text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-0.5"
                        >
                          <i className="ri-reply-line text-xs"></i>
                          回复
                        </button>

                        {canDeleteReply && (
                          <MoreActionMenu
                            align="right"
                            icon={<i className="ri-more-fill text-xs"></i>}
                            buttonClassName="p-1 rounded-md hover:bg-gray-200/60 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center justify-center"
                            menuClassName="w-28"
                            items={[
                              {
                                key: 'delete-reply',
                                label: '删除回复',
                                icon: 'ri-delete-bin-line text-xs',
                                danger: true,
                                onClick: () => handleOpenDeleteSubCommentModal(reply.id || ''),
                              },
                            ]}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* 加载更多二级回复按钮 */}
            {hasMoreReplies && (
              <div className="pt-1">
                <button
                  type="button"
                  onClick={handleLoadMoreReplies}
                  disabled={loadingReplies}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1.5 cursor-pointer py-1 px-2.5 rounded-lg bg-primary-50/60 hover:bg-primary-100/70 transition-colors disabled:opacity-50"
                >
                  {loadingReplies ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xs"></i>
                      <span>加载回复中...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-arrow-down-s-line text-xs"></i>
                      <span>
                        {replies.length > 0
                          ? `展开更多回复 (还剩 ${replyCount - replies.length} 条)`
                          : `展开 ${replyCount} 条回复`}
                      </span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        open={Boolean(deletingSubCommentId)}
        title="删除回复"
        content="确定要删除此回复吗？此操作无法撤销。"
        type="danger"
        confirmText="确定删除"
        cancelText="取消"
        loading={deletingSubCommentLoading}
        onConfirm={handleExecuteDeleteSubComment}
        onCancel={() => setDeletingSubCommentId(null)}
      />
    </div>
  );
}
